import { useRef, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useOnboarding } from '../../../context/OnboardingContext';
import { stripeGlobal, logPurchaseCompletedSuccess } from './StripeHelper';
import { usePaymentStore, paymentStore } from './StripeHelper';
import { getPlanPrice } from '../../../config/plans';
import { trackPurchaseStarted, trackInitiateCheckout } from '../../../services/analytics';
import { createStripeSubscriptionWallet } from '../../../services/api';
import AccordionPanel from './shared/AccordionPanel';
import { currencyCode } from '../../../utils/currencySign';

async function sleepMs(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

const StripeGooglePay = forwardRef(({ isExpanded, onSelect }, ref) => {
    const { userID, selectedPlan, onboardingId, formData, globalAnswers, goToNextScreen, urlParams } = useOnboarding();

    const pm = usePaymentStore();

    const [isProcessing, setIsProcessing] = useState(pm.isProcessing);

    useEffect(() => {
        setIsProcessing(pm.isProcessing);
    }, [pm]);

    let email = formData.get('email') ?? null;

    const createPaymentRequest = () => {

        console.log('google pay: selected plan', selectedPlan);

        const price = getPlanPrice(selectedPlan?.id);

        const pr = stripeGlobal.paymentRequest({
            country: 'US',
            currency: currencyCode().toLowerCase(),
            total: {
                label: selectedPlan.title,
                amount: price,
            },
            requestPayerName: false,
            requestPayerEmail: false,
        });

        pr.on('paymentmethod', async (event) => {

            try {
                const params = {
                    userID: userID,
                    email: event.payerEmail || email,
                    name: event.payerName,
                    priceID: selectedPlan?.priceID,
                    discountID: selectedPlan?.discountID,
                    trialOfferID: selectedPlan?.trialOfferID,
                    paymentMethodId: event.paymentMethod.id,
                    onboardingID: onboardingId,
                    goal: globalAnswers.get('goal')?.value || "unknown",
                    voice: globalAnswers.get('voice')?.value || "unknown",
                    queryParams: urlParams || {}
                }

                const googlePayEndpoint = selectedPlan?.trialOfferID
                    ? "/stripe.createSubscriptionGooglePayWithTrialSupport.php"
                    : "/stripe.createSubscriptionGooglePay.php";
                const response = await createStripeSubscriptionWallet(googlePayEndpoint, params);

                event.complete('success');

                const subscription = await response.json();

                const customerID = subscription.customer ?? null;
                if (customerID) {
                    console.log('google pay: save customerID', customerID);
                    localStorage.setItem('customerID', customerID);
                    localStorage.setItem('paymentProvider', 'stripe');
                }
                else {
                    console.log('google pay: no customerID', subscription, subscription.customer);
                }

                console.log('google pay: subscription', subscription);

                // Проверяем результат создания подписки
                if (subscription.error) {
                    // Показываем ошибку пользователю
                    console.error('google pay: Ошибка создания подписки:', subscription.error);
                    // Здесь можно обновить UI с сообщением об ошибке
                    paymentStore.setState({ isProcessing: false });
                    return;
                }

                if (subscription.status === 'active') {

                    logPurchaseCompletedSuccess(onboardingId, selectedPlan?.priceID, selectedPlan?.discountID, price, urlParams, email);

                    console.log('google pay: subscription active');
                    paymentStore.setState({ isProcessing: false });
                    goToNextScreen();

                } else if (subscription.status === 'incomplete' &&
                    subscription.latest_invoice?.confirmation_secret) {
                    // Нужна дополнительная аутентификация (3D Secure)

                    console.log('google pay: subscription incomplete');

                    const { client_secret } = subscription.latest_invoice.confirmation_secret;
                    const { countryCode } = subscription.rino_countryCode;

                    console.log('google pay: client_secret', client_secret);

                    const { error, paymentIntent } = await stripeGlobal.confirmCardPayment(
                        client_secret
                    );

                    if (error) {
                        // Ошибка при подтверждении платежа
                        console.error('google pay: Ошибка подтверждения:', error);
                        // Обновляем UI с сообщением об ошибке
                    } else if (paymentIntent.status === 'succeeded') {
                        console.log('subscription active 2');

                        paymentStore.setState({ isProcessing: false });
                        logPurchaseCompletedSuccess(onboardingId, selectedPlan?.priceID, selectedPlan?.discountID, price, urlParams, email);

                        goToNextScreen();
                    } else {
                        // Другой статус PaymentIntent
                        console.log('google pay: Статус платежа:', paymentIntent.status);
                    }
                } else {
                    console.log('google pay: Статус подписки:', subscription.status);
                }
            } catch (error) {
                console.error('google pay: Payment error:', error);
                event.complete('fail');
                setPaymentError(error.message || 'Payment failed');
            } finally {
                paymentStore.setState({ isProcessing: false });
            }

        });

        pr.on('paymentfailed', async (event) => {
            console.log('google pay: Payment failed', event);
            setPaymentError(event.error.message || 'Payment failed');
            paymentStore.setState({ isProcessing: false });
        });

        pr.on('paymentauthorized', async (event) => {
            console.log('google pay: Payment authorized', event);
            setIsProcessing(true);
            paymentStore.setState({ isProcessing: false });
        });

        pr.on('cancel', async (event) => {
            console.log('google pay: Payment canceled', event);
            setIsProcessing(false);
            paymentStore.setState({ isProcessing: false });
        });

        return pr;
    }

    const handleGooglePayClick = async () => {

        if (!stripeGlobal) return;

        if (!email) {
            email = prompt("Please enter your email");
        }
    
        paymentStore.setState({ isProcessing: true });
        setIsProcessing(true);
        console.log('google pay: handleGooglePayClick');
        console.log('google pay: isProcessing', isProcessing);

        const paymentRequest = createPaymentRequest();

        const result = await paymentRequest.canMakePayment();

        if (!result || !result.googlePay) {
            console.log('google pay: Google Pay is not available');
            setIsProcessing(false);
            return;
        }

        try {
            trackPurchaseStarted({
                type: 'stripeGooglePay',
                onboardingId,
                priceID: selectedPlan?.priceID,
                discountID: selectedPlan?.discountID,
            });
        } catch (error) {
            console.error('google pay: Error tracking purchase started:', error);
            setIsProcessing(false);
        }

        try {
            trackInitiateCheckout(formData.get('email') ?? "unknown");
        } catch (error) {
            console.error('google pay: Error logging initiate checkout:', error);
        }

        try {
            paymentRequest.show();
        } catch (error) {
            console.error('google pay: Error showing Google Pay:', error);
            setIsProcessing(false);
        }
    };

    // Expose handleGooglePayClick to parent component via ref
    useImperativeHandle(ref, () => ({
        handleGooglePayClick
    }));

    return (
        <section
            className="item"
            data-state={isExpanded ? 'expanded' : 'collapsed'}
            onClick={onSelect}
        >
            <button type="button" className="trigger" aria-expanded={isExpanded}>
                <div className="radio-circle"></div>
                <div className="payment-icons">
                    <div className="image-wrap svelte-grrljh">
                        <img src="/assets/images/gpay2.png" height="30" />
                    </div>
                </div>
            </button>
            <AccordionPanel expanded={isExpanded}>
                <div className="panel" role="region">
                    <div className="content svelte-rwsxt0" style={{ height: 'auto' }}>
                        <div className="content svelte-grrljh">
                            <div className="svelte-1el77kq">
                                <div className="bullets svelte-1op0a8f">
                                    <li className="bullet svelte-1op0a8f">
                                        <svg width="13" height="11" viewBox="0 0 13 11" fill="none">
                                            <path className="checkmark svelte-1op0a8f" d="M11.1997 1.80133L6.99269 8.63774C6.68226 9.14218 5.9932 9.24721 5.54659 8.85815L2.19971 5.94254" strokeWidth="2.66667" strokeLinecap="round" />
                                        </svg>
                                        <span>Easy and private payments with Face/Touch ID</span>
                                    </li>
                                    <li className="bullet svelte-1op0a8f">
                                        <svg width="13" height="11" viewBox="0 0 13 11" fill="none">
                                            <path className="checkmark svelte-1op0a8f" d="M11.1997 1.80133L6.99269 8.63774C6.68226 9.14218 5.9932 9.24721 5.54659 8.85815L2.19971 5.94254" strokeWidth="2.66667" strokeLinecap="round" />
                                        </svg>
                                        <span>Keeps your financial info safe with end-to-end encryption</span>
                                    </li>
                                    <li className="bullet svelte-1op0a8f">
                                        <svg width="13" height="11" viewBox="0 0 13 11" fill="none">
                                            <path className="checkmark svelte-1op0a8f" d="M11.1997 1.80133L6.99269 8.63774C6.68226 9.14218 5.9932 9.24721 5.54659 8.85815L2.19971 5.94254" strokeWidth="2.66667" strokeLinecap="round" />
                                        </svg>
                                        <span>Protected by Google Pay's unique Device Account Number</span>
                                    </li>
                                </div>
                                <div data-testid="stripe-wallet-checkout" className="container svelte-ykx1g">
                                    <button
                                        className="svelte-1wws8ka"
                                        type="button"
                                        onClick={handleGooglePayClick}
                                        disabled={isProcessing}
                                    >
                                        <div className="svelte-lss1u3">
                                            {isProcessing ? (
                                                <span className="button-spinner">
                                                    <svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                                        <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round">
                                                            <animate attributeName="stroke-dasharray" values="1,150;90,150;1,150" dur="1.5s" repeatCount="indefinite" />
                                                            <animate attributeName="stroke-dashoffset" values="0;-35;-124" dur="1.5s" repeatCount="indefinite" />
                                                        </circle>
                                                    </svg>
                                                </span>
                                            ) : (
                                                <>
                                                    Continue with
                                                    <img src="/assets/images/gpay.png" height="30" />
                                                    Pay
                                                </>
                                            )}
                                        </div>
                                    </button>
                                </div>
                                <div className="footer svelte-p3ln32">
                                    <span className="svelte-kfp6vj">Your payment information is secure with SSL/TLS encryption</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </AccordionPanel>
        </section>
    );
});

StripeGooglePay.displayName = 'StripeGooglePay';

export default StripeGooglePay;

